import { JSX } from 'react';
import styled from 'styled-components';
import {
  IconSizeType,
  RiveFileNameType,
  SvgFileNameType,
} from '../../../interfaces/Element.interfaces';
import { RiveAnimation } from '../animations/RiveAnimation';
import { DotBadge } from '../badge/DotBadge';
import { Icon } from '../icon/Icon';

// TODO : Button 컴포넌트의 미구현된 기능 (Loading, Tooltip) 구현 https://gint-team.atlassian.net/browse/PVM-1770

const buttonColors = {
  filledGreyStrong: { background: '#505363', text: '#FFF', outline: 'none' },
  filledPrimaryStrong: { background: '#445EFF', text: '#FFF', outline: 'none' },
  filledSecondaryStrong: {
    background: '#FF3049',
    text: '#FFF',
    outline: 'none',
  },
  filledTertiaryStrong: {
    background: '#FF9500',
    text: '#FFF',
    outline: 'none',
  },
  filledQuateraryStrong: {
    background: '#34C77E',
    text: '#FFF',
    outline: 'none',
  },
  filledWhiteStrong: { background: '#FFF', text: '#505363', outline: 'none' },
  filledPrimaryLight: {
    background: '#E5E9FF',
    text: '#445EFF',
    outline: 'none',
  },
  filledSecondaryLight: {
    background: '#FFDBE0',
    text: '#FF3049',
    outline: 'none',
  },
  filledGreyLight: { background: '#E4E4ED', text: '#505363', outline: 'none' },
  filledWhiteLight: { background: '#FFF', text: '#858894', outline: 'none' },
  outlinedPrimaryStrong: {
    background: 'none',
    text: '#445EFF',
    outline: '#445EFF',
  },
  outlinedGreyLight: {
    background: 'none',
    text: '#505363',
    outline: '#E4E4ED',
  },
  nakedGreyLight: { background: 'none', text: '#858894', outline: 'none' },
  nakedGreyStrong: { background: 'none', text: '#505363', outline: 'none' },
  nakedSecondaryStrong: {
    background: 'none',
    text: '#FF3049',
    outline: 'none',
  },
} as const;

const buttonSizes = {
  xs: {
    height: '22px',
    padding: '6px 8px',
    gap: '2px',
  },
  sm: {
    height: '32px',
    padding: '6px 8px',
    gap: '4px',
  },
  md: {
    height: '40px',
    padding: '16px',
    gap: '4px',
  },
  lg: {
    height: '44px',
    padding: '16px',
    gap: '4px',
  },
} as const;

const buttonTextSizes = {
  sm: '12px',
  md: '14px',
  lg: '16px',
} as const;

export type ButtonColorType = keyof typeof buttonColors;
type ButtonSizeType = keyof typeof buttonSizes;
type ButtonTextSizeType = keyof typeof buttonTextSizes;
type ButtonBorderType = 'square' | 'round';

interface IButtonIcon {
  position: 'left' | 'right';
  size: IconSizeType;
  fileName: SvgFileNameType;
  fill?: string;
}

interface IButtonRiveAnimation {
  name: RiveFileNameType;
  size: IconSizeType;
}

interface ButtonProps {
  id?: string;
  icon?: IButtonIcon;
  riveAnimation?: IButtonRiveAnimation;
  borderType: ButtonBorderType;
  isDisabled?: boolean;
  color: ButtonColorType;
  size: ButtonSizeType;
  text?: {
    size: ButtonTextSizeType;
    content: string;
  };
  hasNotification?: boolean;
  onClick: () => void;
}

export const Button = ({
  id,
  icon,
  riveAnimation,
  borderType,
  isDisabled = false,
  color,
  size,
  text,
  hasNotification = false,
  onClick,
}: ButtonProps): JSX.Element => {
  const ButtonIcon: JSX.Element = icon ? (
    <Icon name={icon.fileName} size={icon.size} fill={icon.fill} />
  ) : (
    <></>
  );
  const ButtonRiveAnimation: JSX.Element = riveAnimation ? (
    <RiveAnimation name={riveAnimation.name} size={riveAnimation.size} />
  ) : (
    <></>
  );

  return (
    <ColoredButton
      id={id}
      disabled={isDisabled}
      $borderType={borderType}
      color={color}
      size={size}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <ButtonContentContainer size={size}>
        {riveAnimation && ButtonRiveAnimation}
        {icon && icon.position === 'left' && ButtonIcon}
        {text && (
          <ButtonText color={color} $textSize={text.size}>
            {text.content}
          </ButtonText>
        )}
        {icon && icon.position === 'right' && ButtonIcon}
        {hasNotification && <DotBadge style={{ right: '-8.5px' }} />}
      </ButtonContentContainer>
    </ColoredButton>
  );
};

const ColoredButton = styled.button<{
  $borderType: ButtonBorderType;
  color: ButtonColorType;
  size: ButtonSizeType;
}>`
  height: ${(props) => buttonSizes[props.size].height};
  max-height: ${(props) => buttonSizes[props.size].height};

  padding: ${(props) => buttonSizes[props.size].padding};

  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  border-radius: ${(props) =>
    props.$borderType === 'square'
      ? '8px'
      : props.$borderType === 'round'
      ? '80px'
      : '0px'};

  background-color: ${(props) => buttonColors[props.color].background};

  ${(props) =>
    buttonColors[props.color].outline !== 'none' &&
    `border: 1px solid ${buttonColors[props.color].outline};`}

  &:disabled {
    opacity: 0.3;
  }
`;

const ButtonText = styled.span<{
  color: ButtonColorType;
  $textSize: ButtonTextSizeType;
}>`
  color: ${(props) => buttonColors[props.color].text};

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: ${(props) => buttonTextSizes[props.$textSize]};
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */

  white-space: nowrap;
`;

const ButtonContentContainer = styled.div<{ size: ButtonSizeType }>`
  position: relative;

  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;

  gap: ${(props) => buttonSizes[props.size].gap};
`;
